import { george, observer, pr, React, RouteComponentProps, theme, ui } from "aaa";
import { AccessibleDropdown } from "components/common/AccessibleDropdown";
import { InfoBoxWithBackground } from "components/common/InfoBoxWithBackground";
import { STYLE_CENTERED_CARD } from "components/util/primitives";
import { themeColors } from "muiTheme";
import externalInterfaceStore from "stores/ExternalInterfaceStore";
import { pushRoute } from "stores/RouterFunctions";
import { IAccount } from "types/account";
import { formatIban } from "util/helpers";
import { TPP } from "./TPP";
import { TPPEmptyList } from "./TPPEmptyList";
import { TPPRoutes } from "./TPPRoutes";
import { TPPStore } from "./TPPStore";

type IProps = RouteComponentProps<{}> & {
    store: TPPStore
};

export const TPPList = observer(({ store }: IProps) => {
    const [hoveredOption, setHoveredOption] = React.useState<IAccount | undefined>(undefined);

    const handleMouseEnterOption = (option: IAccount) => {
        setHoveredOption(option);
    };

    const handleMouseLeaveOption = () => {
        setHoveredOption(undefined);
    };

    const handleChangeDefaultAccount = (options: IAccount[]) => {
        store.newDefaultAccount = options[0];
    };

    const handleSubmit = async () => {
        try {
            await store.updateDefaultAccount();
            pushRoute(TPPRoutes.SIGNING);
        } catch (error) {
            pushRoute("/error");
        }
    };

    const hasTPPS = store.userManagementConsentsResponse && store.userManagementConsentsResponse.length > 0;

    if (store.isLoading()) {
        return <george.LoadingIndicator isLoading height={ui.windowHeight} />;
    }

    const PageComponent = george.PageWithActionButton;

    const contentPadding = ui.windowWidth < 800 ? 24 : 32;

    const disableButton = !store.newDefaultAccount || store.newDefaultAccount === store.defaultAccount;

    return (
        <george.Background style={{ boxSizing: "border-box", paddingTop: 32 }}>
            <PageComponent>
                {!hasTPPS && <TPPEmptyList useCard />}

                <pr.A11yPopupHider>
                    <george.Card
                        disableTopSpacing
                        style={{
                            marginTop: !hasTPPS ? theme.MARGIN : 0,
                            borderRadius: 8,
                            boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.05)",
                            ...STYLE_CENTERED_CARD
                        }}
                    >
                        {hasTPPS && <>
                            <div style={{ padding: contentPadding }}>
                                <pr.P_22_TITLE style={{ color: ui.isDarkMode ? "white" : "#0A285C", fontWeight: 700, lineHeight: "28px" }}>
                                    {ui.__("tpp.list.title")}
                                </pr.P_22_TITLE>
                                <pr.P_16_CONTENT style={{ color: ui.isDarkMode ? "white" : "#0A285C", marginTop: 8 }}>
                                    {ui.__("tpp.list.disclaimer")}
                                </pr.P_16_CONTENT>
                                <div style={{ marginTop: theme.MARGIN }}>
                                    {store.userManagementConsentsResponse?.map((consent, index) => {
                                        return (
                                            <TPP
                                                key={consent.id}
                                                onClick={() => {
                                                    store.currentConsent = consent;
                                                    pushRoute(TPPRoutes.DETAILS);
                                                }}
                                                consent={consent}
                                                hasDetails
                                                separator={index < (store.userManagementConsentsResponse?.length ?? 0) - 1}
                                            />
                                        );
                                    })}
                                </div>
                                <InfoBoxWithBackground
                                    borderColor="#135EE2"
                                    backgroundColor={ui.isDarkMode ? undefined : "#E7EFFD"}
                                    iconSize={16}
                                    iconColor="#135EE2"
                                    style={{ fontSize: 14, marginTop: 8 }}
                                >
                                    <pr.P_14_CONTENT style={{ color: ui.isDarkMode ? undefined : "#0A285C" }}>
                                        {ui.__("tpp.list.infoBox")}
                                    </pr.P_14_CONTENT>
                                </InfoBoxWithBackground>
                            </div>
                            <pr.LightSeparatorLine />
                        </>}
                        <div style={{ padding: contentPadding }}>
                            <pr.P_16_CONTENT style={{ color: ui.isDarkMode ? "white" : "#0A285C" }}>
                                {ui.__("tpp.list.defaultAccount.description")}
                            </pr.P_16_CONTENT>
                            <george.ResponsiveFormElement
                                label={ui.__("tpp.list.defaultAccount.label")}
                                style={{ alignItems: "center", marginTop: 16 }}
                                elementStyle={{ flexShrink: 0, flexGrow: 0 }}
                            >
                                <AccessibleDropdown
                                    options={store.accountsStore.accounts}
                                    valueKey="id"
                                    single
                                    showDivider={false}
                                    defaultValues={store.defaultAccount ? [store.defaultAccount] : undefined}
                                    dropDownStyle={{
                                        overflowY: "auto",
                                        width: "100%",
                                        maxWidth: "100%",
                                        maxHeight: 240,
                                        backgroundColor: ui.isDarkMode ? themeColors.d_grey_300 : "white"
                                    }}
                                    openVariant={externalInterfaceStore.isGeorgeApp ? "top" : "bottom"}
                                    onChange={handleChangeDefaultAccount}
                                    valueRenderer={(values) => {
                                        if (values.length === 0) {
                                            return ui.__("tpp.list.defaultAccount.placeholder");
                                        }

                                        const formattedIban = formatIban(store.newDefaultAccount?.accountno.iban);

                                        return (
                                            <span style={{ whiteSpace: "nowrap" }}>
                                                {store.newDefaultAccount?.alias || store.newDefaultAccount?.description}, {formattedIban}
                                            </span>
                                        );
                                    }}
                                    optionRenderer={(option, isSelected) => {
                                        const hoverColor = ui.isDarkMode ? "rgba(238, 244, 254, 0.1)" : "#EEF4FE";

                                        return (
                                            <div
                                                onMouseEnter={() => handleMouseEnterOption(option)}
                                                onMouseLeave={handleMouseLeaveOption}
                                                style={{
                                                    boxSizing: "border-box",
                                                    height: 84,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    position: "relative",
                                                    padding: "12px 20px",
                                                    overflow: "hidden",
                                                    backgroundColor: hoveredOption === option || isSelected
                                                        ? hoverColor
                                                        : undefined
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        position: "absolute",
                                                        left: 0,
                                                        top: 0,
                                                        bottom: 0,
                                                        width: 4,
                                                        backgroundColor: (
                                                            ui.isDarkMode
                                                                ? option.additionalData?.accountColor?.hexCodeDark
                                                                : option.additionalData?.accountColor?.hexCode
                                                        ) ?? "#FFD32A"
                                                    }}
                                                />
                                                <div style={{ overflow: "hidden", marginLeft: 16 }}>
                                                    <pr.P_14_CONTENT style={{ lineHeight: "20px" }}>
                                                        {option.productI18N}
                                                    </pr.P_14_CONTENT>
                                                    <pr.P_16_TITLE
                                                        style={{
                                                            fontWeight: "bold",
                                                            lineHeight: "24px",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap"
                                                        }}
                                                    >
                                                        {option.alias || option.description}
                                                    </pr.P_16_TITLE>
                                                    <pr.P_14_CONTENT style={{ lineHeight: "20px", marginTop: 2 }}>
                                                        {formatIban(option.accountno.iban)}
                                                    </pr.P_14_CONTENT>
                                                </div>
                                            </div>
                                        );
                                    }}
                                />
                            </george.ResponsiveFormElement>
                        </div>
                        {!externalInterfaceStore.isGeorgeApp &&
                            <pr.LightSeparatorLine style={{ marginBottom: theme.MARGIN }} />
                        }
                        <george.ActionButton
                            messageId="button.confirm"
                            disableButton={disableButton}
                            onClick={handleSubmit}
                            {...george.getActionButtonWebVersionProps()}
                        />
                    </george.Card>
                </pr.A11yPopupHider>

            </PageComponent>
        </george.Background>
    );
});



// WEBPACK FOOTER //
// ./src/components/tpp/TPPList.tsx